import {
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  createComponent,
  forwardRef
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { Popover } from "primeng/popover";
import { ISearchQueryEmployee, Pagination, SearchQueryResult, VisibilityName, WorkHoursType } from "@vierkant-software/types__api";
import { VPersonSearchComponent } from "../component/v-person-search.component";
import { SearchBaseComponent, SearchField, SelectMode } from "../component/v-person-search.base.component";

/**
 * Person and user search as dropdown
 * 
 * @name    Person Search Dropdown
 * @tags    User, Input, ControlValueAccessor
 * @remark  Is included in the v-person-search-module
 * @returns string[] User IDs
 * @example 
 * ```
 * %v-person-search{[visibility]: "VisibilityName.person_search"}
 * ```
 */
@Component({
  selector:      'v-person-search-dropdown',
  templateUrl:   './v-person-search-dropdown.component.haml',
  styleUrls:     ['./../component/v-person-search.component.sass', './v-person-search-dropdown.component.sass'],
  encapsulation: ViewEncapsulation.None,
  providers:     [
    {
      provide:     NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VPersonSearchDropdownComponent),
      multi:       true
    }
  ],
  standalone: false
})
export class VPersonSearchDropdownComponent extends SearchBaseComponent implements OnDestroy {
  private static instances: VPersonSearchDropdownComponent[] = [];
  public static close(){
    VPersonSearchDropdownComponent.instances.forEach(x => x.overlay.hide());
  }

  @Output() public selectionChange = new EventEmitter<string[]>();
  @Output() public selectedItems = new EventEmitter<SearchQueryResult[]>();
  /**
   * Filter by employment
   * @values  ISearchQueryEmployee
   * @example 
   * ```ts
   *  interface ISearchQueryEmployee {
   *    isEmployee: boolean;
   *    formerEmployees: boolean;
   *    activeEmployees: boolean;
   *    futureEmployees: boolean;
   *  }
   * ```
   */
  @Input() public  employment?: Partial<ISearchQueryEmployee>;
  /**
   * Placeholder for the text-input
   */
  @Input() public placeholder: string = "PLACEHOLDER.SEARCH";
  /**
   * Allow department filter.
   * @remark  Pass department IDs as string array, to limit the available filter. 
   *          Otherwise all visible departments of the current users will be listed
   * @values  string[] | true
   */
  @Input() public  departments?: string[] | true;
  /**
   * Available search fields
   */
  @Input() public  fields?: SearchField[];
  /**
   * Active search fields
   */
  @Input() public  activeFields?: SearchField[];
  /**
   * Locked search fields
   */
  @Input() public  lockedFields?: SearchField[];
  /**
   * Filter by trusted work times
   * @values "yes" | "no" | "any"
   */
  @Input() public trustedWorkTimes?:  "yes" | "no" | "any";
  /**
   * Filter by workhoursType
   * @values WorkHoursType
   */
  @Input() public workhoursType?: WorkHoursType;
  /**
   * Control loading state
   */
  @Input() public  loading: boolean;
  /**
   * Visibility of the search.
   * @values VisibilityName
   */
  @Input() public  visibility?: VisibilityName;
  /**
   * Limit max selectable amount.
   */
  @Input() public  maxSelect?: number;
  /**
   * Require min selectable amount.
   */
  @Input() public  minSelect?: number;
  @ViewChild('op', {static: true}) public overlay: Popover;
  /**
   * Pagination object
   * 
   * @example
   * ```ts
   *  const pagination = {
   *    page: number;
   *    size: number;
   *  }
   * ```
   */
  @Input() public  pagination: Pagination;
  @ViewChild('popup', {static: true}) public searchElement: ElementRef<HTMLElement>;
  @ViewChild('search', {static: true}) public inputElement: ElementRef<HTMLElement>;
  private _searchValue: string;
  public get searchValue(): string {
    return this._searchValue;
  }
  /**
   * Search term
   */
  @Input() public set searchValue(value: string) {
    this._searchValue = value;
    this.searchComponent.setInput("searchValue", this.searchValue);
  }
  /**
   * Control selection of one or multiple people.
   * 
   * @values  "single" | "multiple"
   * @default "single"
   */
  @Input() public  selectionMode: SelectMode = "single";
  /**
   * Show avatar of people
   */
  @Input() public  showAvatar: boolean;
  @Input() public  excludeUsers: string[];

  public showMask: boolean;

  public get searchResult(){
    return this._searchComponent?.instance.searchResult;
  }
  public get avatars(){
    return this._searchComponent?.instance.avatars;
  }
  private _searchComponent: ComponentRef<VPersonSearchComponent>;
  public get searchComponent(): ComponentRef<VPersonSearchComponent> {
    if (!this._searchComponent){
      this._searchComponent = createComponent(VPersonSearchComponent, {
        environmentInjector: this.injector,
      });
      this._searchComponent.instance.onChange = (x: string[]) => this.value = x;
      this.appRef.attachView(this._searchComponent.hostView);
    }
    return this._searchComponent;
  }
  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ){
    super();
    VPersonSearchDropdownComponent.instances.push(this);
  }

  public ngOnDestroy(): void {
    const index = VPersonSearchDropdownComponent.instances.findIndex(x => x === this);
    if (index >= 0) VPersonSearchDropdownComponent.instances.splice(index, 1);
  }

  public showOverlay(){
    this.showMask = true;
    this.overlay.show({
      currentTarget: this.inputElement.nativeElement,
      target:        this.inputElement.nativeElement,
    });
  }
  public hideOverlay(){
    this.showMask = false;
    this.overlay.hide();
  }

  onPopupShow(event: unknown){
    this.searchComponent.setInput("value", this.value);
    this.searchComponent.setInput("searchValue", this.searchValue);
    this.searchComponent.setInput("departments", this.departments);
    this.searchComponent.setInput("employment", this.employment);
    this.searchComponent.setInput("fields", this.fields);
    this.searchComponent.setInput("activeFields", this.activeFields);
    this.searchComponent.setInput("lockedFields", this.lockedFields);
    this.searchComponent.setInput("trustedWorkTimes", this.trustedWorkTimes);
    this.searchComponent.setInput("workhoursType", this.workhoursType);
    this.searchComponent.setInput("pagination", this.pagination);
    this.searchComponent.setInput("visibility", this.visibility);
    this.searchComponent.setInput("minSelect", this.minSelect);
    this.searchComponent.setInput("maxSelect", this.maxSelect);
    this.searchComponent.setInput("selectionMode", this.selectionMode);
    this.searchComponent.setInput("showAvatar", this.showAvatar);
    this.searchComponent.setInput("excludeUsers", this.excludeUsers);
    this.searchComponent.instance.selectionChange = this.selectionChange;
    this.searchComponent.instance.selectedItems = this.selectedItems;
    this.overlay.container.firstChild.appendChild(this.searchComponent.location.nativeElement);
  }

  onPopupHide(event: unknown) {
    this.showMask = false;
  }

  public clear(): void {
    super.clear();
    this.searchComponent.setInput("searchValue", this.searchValue);
  }
}
